import { heroRotation, profile, stats } from '../../data/site';
import { useTypewriter } from '../../hooks/useScrollFx';

function scrollToId(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/*--------------------
* Aurora background
----------------------*/
function Aurora() {
  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
      <div className="grid-lines absolute inset-0" />
      <div
        className="aurora animate-drift h-[520px] w-[520px] bg-violet-brand/30 -top-[140px] -left-[80px]"
        style={{ animationDelay: '0s' }}
      />
      <div
        className="aurora animate-drift h-[480px] w-[480px] bg-cyan-brand/20 top-[40px] right-[-100px]"
        style={{ animationDelay: '-7s' }}
      />
      <div
        className="aurora animate-drift h-[400px] w-[400px] bg-pink-brand/20 bottom-[-120px] left-[35%]"
        style={{ animationDelay: '-14s' }}
      />
      {/* fade into the next section */}
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-canvas" />
    </div>
  );
}

/*--------------------
* Banner / Hero
----------------------*/
export default function Banner() {
  const rotated = useTypewriter(heroRotation);

  return (
    <section id="home" className="relative overflow-hidden pt-[130px] pb-[70px] lg:pt-[190px] lg:pb-[110px]">
      <Aurora />

      <div className="container relative z-[1]">
        <div className="max-w-[900px]">
          {/* Availability badge */}
          <div className="reveal inline-flex items-center gap-3 rounded-full border border-tint/[.1] bg-tint/[.04] py-1.5 pl-1.5 pr-4 backdrop-blur-xl">
            <span className="relative flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-violet-brand/70 to-cyan-brand/50">
              <img
                src={profile.avatar}
                alt={profile.name}
                className="h-full w-full object-cover"
              />
            </span>
            <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[.16em] text-chalk">
              <i className="dot-live" />
              {profile.availability}
            </span>
          </div>

          {/* Headline */}
          <h1
            className="reveal mt-7 font-display text-[38px] font-[600] leading-[1.06] tracking-[-.03em] text-chalk sm:text-[54px] lg:text-[76px]"
            style={{ transitionDelay: '.08s' }}
          >
            I build{' '}
            <span className="grad-text">AI-powered</span>
            <br className="hidden sm:block" /> mobile apps{' '}
            <span className="whitespace-nowrap">
              {rotated}
              <span className="ml-0.5 inline-block h-[.85em] w-[3px] translate-y-[.08em] bg-violet-brand align-middle animate-blink" />
            </span>
          </h1>

          {/* Lede */}
          <p
            className="reveal lede mt-7 text-[17px] lg:text-[19px]"
            style={{ transitionDelay: '.16s' }}
          >
            {profile.summary}
          </p>

          {/* CTAs */}
          <div
            className="reveal mt-9 flex flex-wrap items-center gap-3"
            style={{ transitionDelay: '.24s' }}
          >
            <button className="btn btn-primary" onClick={() => scrollToId('contact')}>
              Let&rsquo;s talk
              <span aria-hidden="true">&rarr;</span>
            </button>
            <a
              className="btn btn-ghost"
              href={`${process.env.PUBLIC_URL}/${profile.cv}`}
              target="_blank"
              rel="noreferrer"
            >
              Download CV
              <span aria-hidden="true">&darr;</span>
            </a>
            <button className="btn btn-ghost" onClick={() => scrollToId('work')}>
              See the work
            </button>
          </div>
        </div>

        {/* Stats bento */}
        <div className="mt-16 grid grid-cols-2 gap-3 lg:mt-20 lg:grid-cols-4 lg:gap-4">
          {stats.map((s, i) => (
            <div
              key={s.label}
              className="reveal glass glass-hover card-sheen p-5 lg:p-6"
              style={{ transitionDelay: `${0.3 + i * 0.07}s` }}
            >
              <div className="font-display text-[24px] font-[600] leading-none tracking-[-.02em] text-chalk lg:text-[30px]">
                {s.value}
              </div>
              <div className="mt-2.5 text-[13px] leading-[1.45] text-muted">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
